import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Save, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ImagePicker from "./ImagePicker";

const KIND_OPTIONS = [
    { value: "white", label: "Standard (white)" },
    { value: "amber", label: "Highlight (amber)" },
];

const blankForm = { title: "", body: "", kind: "white", slug: "", active: true, image_url: "" };

const DisclosuresPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(blankForm);
    const [adding, setAdding] = useState(false);
    const [showPicker, setShowPicker] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/disclosures");
            setRows(data || []);
        } catch (e) {
            toast.error("Could not load disclosures");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const startEdit = (r) => {
        setEditingId(r.id);
        setAdding(false);
        setForm({ title: r.title, body: r.body, kind: r.kind, slug: r.slug, active: r.active, image_url: r.image_url || "" });
    };

    const cancelEdit = () => { setEditingId(null); setAdding(false); setForm(blankForm); };

    const save = async () => {
        if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body are required."); return; }
        try {
            if (adding) {
                await api.post("/admin/disclosures", form);
                toast.success("Disclosure added");
            } else {
                await api.patch(`/admin/disclosures/${editingId}`, form);
                toast.success("Disclosure saved");
            }
            cancelEdit();
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (r) => {
        const msg = r.is_seed
            ? `Hide "${r.title}"? Seed disclosures are kept (soft-deleted) so they can be re-activated later.`
            : `Permanently delete "${r.title}"?`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/admin/disclosures/${r.id}`);
            toast.success(r.is_seed ? "Hidden" : "Deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const toggleActive = async (r) => {
        try {
            await api.patch(`/admin/disclosures/${r.id}`, { active: !r.active });
            await load();
        } catch (e) {
            toast.error("Could not update visibility");
        }
    };

    const reorder = async (idx, delta) => {
        const j = idx + delta;
        if (j < 0 || j >= rows.length) return;
        const a = rows[idx], b = rows[j];
        try {
            await api.post("/admin/disclosures/reorder", {
                order: [
                    { id: a.id, order: b.order },
                    { id: b.id, order: a.order },
                ],
            });
            await load();
        } catch (e) {
            toast.error("Reorder failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="disclosures-panel">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Governance &amp; disclosures</h2>
                    <p className="text-sm text-[#4A5568] mt-1">
                        These cards appear on the public <code>/about#governance</code> page in the order shown. Use <strong>**bold**</strong> to emphasise critical wording. Visibility, ordering, and copy are fully editable.
                    </p>
                </div>
                <button
                    onClick={() => { setAdding(true); setEditingId(null); setForm(blankForm); }}
                    data-testid="disclosure-add-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add disclosure
                </button>
            </div>

            {(adding || editingId) && (
                <div className="bg-white border border-[#E53935]/30 rounded-2xl p-5 space-y-3" data-testid="disclosure-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label htmlFor="disc-title" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Title</label>
                            <input
                                id="disc-title"
                                type="text"
                                placeholder="Card title"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                maxLength={120}
                                data-testid="disclosure-title-input"
                                className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                            />
                        </div>
                        <div>
                            <label htmlFor="disc-slug" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Anchor slug</label>
                            <input
                                id="disc-slug"
                                type="text"
                                placeholder="auto from title"
                                value={form.slug || ""}
                                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                maxLength={60}
                                data-testid="disclosure-slug-input"
                                className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] font-mono text-sm"
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="disc-body" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Body (supports **bold**)</label>
                        <textarea
                            id="disc-body"
                            rows={6}
                            placeholder="The visible copy of the card"
                            value={form.body}
                            onChange={(e) => setForm({ ...form, body: e.target.value })}
                            maxLength={4000}
                            data-testid="disclosure-body-input"
                            className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                        <p className="text-xs text-[#4A5568] mt-1">{form.body.length}/4000</p>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Image (optional, shown above the body)</label>
                        <div className="flex gap-2 mt-1">
                            <input
                                placeholder="Paste image URL or pick from library"
                                value={form.image_url}
                                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                data-testid="disclosure-image-input"
                                className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                            />
                            <button type="button" onClick={() => setShowPicker(true)} data-testid="disclosure-pick-library" className="px-4 py-2.5 rounded-xl border border-black/10 hover:bg-[#F4F1EA] text-sm font-medium whitespace-nowrap">
                                Pick from library
                            </button>
                            {form.image_url && (
                                <button type="button" onClick={() => setForm({ ...form, image_url: "" })} data-testid="disclosure-clear-image" className="px-3 py-2.5 rounded-xl border border-black/10 hover:bg-red-50 text-sm">
                                    <X className="w-4 h-4 text-[#E53935]" strokeWidth={2} />
                                </button>
                            )}
                        </div>
                        {form.image_url && (
                            <img src={form.image_url} alt="preview" className="mt-2 max-h-32 rounded-xl border border-black/5 object-cover bg-[#F4F1EA]" />
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <label htmlFor="disc-kind" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Style</label>
                            <select
                                id="disc-kind"
                                value={form.kind}
                                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                                data-testid="disclosure-kind-select"
                                className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                            >
                                {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 mt-5 text-sm text-[#1C252E]">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                data-testid="disclosure-active-checkbox"
                            /> Visible on public page
                        </label>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={cancelEdit} className="inline-flex items-center gap-1 text-sm font-medium text-[#4A5568] hover:text-[#1C252E]">
                            <X className="w-4 h-4" strokeWidth={2} /> Cancel
                        </button>
                        <button onClick={save} data-testid="disclosure-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium">
                            <Save className="w-4 h-4" strokeWidth={2} /> {adding ? "Add disclosure" : "Save changes"}
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="text-sm text-[#4A5568]">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="disclosures-empty">No disclosures yet.</p>
            ) : (
                <ul className="space-y-2" data-testid="disclosures-list">
                    {rows.map((r, idx) => (
                        <li
                            key={r.id}
                            data-testid={`disclosure-row-${r.slug}`}
                            className={`p-4 rounded-2xl border ${r.active ? "bg-white border-black/10" : "bg-[#F4F1EA]/50 border-black/5 opacity-70"}`}
                        >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F4F1EA] text-[10px] font-bold text-[#1C252E]">{idx + 1}</span>
                                    <p className="font-medium text-[#1C252E] truncate">{r.title}</p>
                                    {r.kind === "amber" && (
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded-full">amber</span>
                                    )}
                                    {!r.active && (
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] bg-[#F4F1EA] px-2 py-0.5 rounded-full">hidden</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => reorder(idx, -1)} disabled={idx === 0} data-testid={`disclosure-up-${r.slug}`} aria-label="Move up" className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-30">
                                        <ArrowUp className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => reorder(idx, +1)} disabled={idx === rows.length - 1} data-testid={`disclosure-down-${r.slug}`} aria-label="Move down" className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] disabled:opacity-30">
                                        <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => toggleActive(r)} data-testid={`disclosure-toggle-${r.slug}`} aria-label={r.active ? "Hide" : "Show"} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]">
                                        {r.active ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                                    </button>
                                    <button onClick={() => startEdit(r)} data-testid={`disclosure-edit-${r.slug}`} aria-label="Edit" className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]">
                                        <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => remove(r)} data-testid={`disclosure-delete-${r.slug}`} aria-label="Delete" className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>
                            <p className="text-xs text-[#4A5568] mt-2 line-clamp-2 whitespace-pre-line">{r.body}</p>
                            <p className="text-[10px] text-[#4A5568] mt-1 font-mono">/about#{r.slug}</p>
                        </li>
                    ))}
                </ul>
            )}
            <ImagePicker
                open={showPicker}
                onClose={() => setShowPicker(false)}
                onPick={(img) => setForm((prev) => ({ ...prev, image_url: img.url }))}
                category="misc"
            />
        </div>
    );
};

export default DisclosuresPanel;
